import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** `DataTable` 表头行高（其内联 style 为 `height: 50px`） */
const DATA_TABLE_HEADER_HEIGHT = 50;
/** `DataTable` 无数据时的占位行高（`<TableCell colSpan className="h-24" />`） */
const DATA_TABLE_EMPTY_ROW_HEIGHT = 96;

export interface PreviewPageSkeletonProps {
  /** 账户切换 Tab 占位数量 */
  tabCount?: number;
  /** 预览表格占位列数 */
  columnCount?: number;
  /** 底部操作按钮数量 */
  actionCount?: number;
  className?: string;
}

/**
 * 预览确认页骨架：账户切换 Tab + 汇总说明行 + 预览表格 + 底部操作栏。
 *
 * 结构对齐 `AddAdV2/AddADPreview`。这里的 Card 是 `flex-1`（真实页面即如此，加载中也是整张卡片撑满），
 * 但表格区只画「表头 + 单个空行」——与 `DataTable` 的加载 / 空态高度一致，
 * 避免骨架屏画满行、真实页面加载中塌下去、数据到达又撑回来的上下跳动。
 */
export function PreviewPageSkeleton({
  tabCount = 3,
  columnCount = 6,
  actionCount = 3,
  className,
}: PreviewPageSkeletonProps) {
  return (
    <div
      data-testid="preview-page-skeleton"
      className={cn('w-full h-full flex flex-col gap-2', className)}
    >
      <Card className="flex-1 flex flex-col overflow-hidden p-4">
        <CardContent className="flex-1 flex flex-col p-0 overflow-hidden gap-2">
          {/* 账户切换 Tab 行 */}
          <div className="border-b">
            <div className="flex items-center gap-4 pb-2">
              {Array.from({ length: tabCount }, (_, index) => (
                <Skeleton key={index} className="h-5 w-28" />
              ))}
            </div>
          </div>

          {/* 汇总说明行（推广系列 / 广告组 / 广告数量） */}
          <div className="py-2">
            <Skeleton className="h-5 w-72" />
          </div>

          {/* 预览表格：表头 + 单个空行（对齐 DataTable 的加载 / 空态高度） */}
          <div className="min-h-0 flex-1 overflow-hidden">
            <div
              className="flex items-center gap-4 border-y bg-muted px-4"
              style={{ height: DATA_TABLE_HEADER_HEIGHT }}
            >
              {/* 本主题 --muted 与 --accent 同色，列占位块必须显式给对比色才看得见 */}
              {Array.from({ length: columnCount }, (_, index) => (
                <Skeleton key={index} className="h-4 flex-1 bg-[#e4e4e4] dark:bg-[#3a3a3a]" />
              ))}
            </div>
            <div className="border-b" style={{ height: DATA_TABLE_EMPTY_ROW_HEIGHT }} />
          </div>
        </CardContent>
      </Card>

      {/* 底部操作栏：左侧选中统计 + 右侧按钮组 */}
      <div className="flex shrink-0 items-center justify-between px-6 py-4">
        <Skeleton className="h-5 w-32" />
        <div className="flex items-center gap-4">
          {Array.from({ length: actionCount }, (_, index) => (
            <Skeleton key={index} className="h-8 w-24" />
          ))}
        </div>
      </div>
    </div>
  );
}
